<template>
  <div class="app-container">
    <!-- Language Switcher -->
    <div class="language-switcher">
      <button @click="toggleLanguage" class="lang-btn">
        <i class="fas fa-globe"></i>
        <span>{{ langText }}</span>
      </button>
    </div>

    <div class="container">
      <header>
        <h1><i class="fas fa-mobile-alt"></i> {{ t('appDownloadCenter') }}</h1>
        <p class="subtitle">{{ t('scanToDownload') }}</p>
        <p>
          {{ t('platformDescription') }}
          <span :class="['api-status', apiStatus.class]">{{ t(apiStatus.text) }}</span>
        </p>
      </header>

      <div class="app-info">
        <!-- Screenshot Section -->
        <div class="screenshot-section">
          <h2 class="section-title">Android {{ t('appScreenshots') }}</h2>
          <div class="device-frame">
            <div class="screenshot-container">
              <img :src="currentScreenshot" :alt="t('screenshotAlt')" class="screenshot" />
            </div>
          </div>
          <div class="screenshot-nav">
            <div v-for="(screenshot, index) in screenshots" :key="index" class="nav-dot"
              :class="{ active: currentScreenshotIndex === index }" @click="changeScreenshot(index)"></div>
          </div>
        </div>

        <!-- IOS 应用截图 -->
        <div class="screenshot-section">
          <h2 class="section-title">IOS {{ t('appScreenshots') }}</h2>
          <div class="device-frame">
            <div class="screenshot-container">
              <img :src="iosCurrentScreenshot" :alt="t('screenshotAlt')" class="screenshot" />
            </div>
          </div>
          <div class="screenshot-nav">
            <div v-for="(screenshot, index) in iosScreenshots" :key="index" class="nav-dot"
              :class="{ active: iosCurrentScreenshotIndex === index }" @click="iosChangeScreenshot(index)"></div>
          </div>
        </div>


        <!-- Download Section -->
        <div class="download-section">
          <h2 class="section-title">{{ t('downloadApp') }}</h2>
          <!-- <p class="app-description">{{ appDescription }}</p> -->

          <div class="download-info">
            <p>{{ t('appName') }}: <span class="version-info">{{ appData.appName }}</span></p>
            <p>
              {{ t('version') }}: <span class="version-info">{{ appData.version }}</span>
              <span v-if="appData.versionCode">({{ appData.versionCode }})</span>
            </p>
            <p>{{ t('platform') }}: <span class="size-info">{{ appData.platform }}</span></p>
            <p>{{ t('updated') }}: <span class="update-time">{{ formattedUpdateTime }}</span></p>
            <div class="update-badges">
              <span v-if="appData.forceUpdate" class="force-update-badge">{{ t('forceUpdate') }}</span>
              <span v-if="appData.silentUpdate" class="silent-update-badge">{{ t('silentUpdate') }}</span>
            </div>
          </div>

          <div class="qr-container">
            <!-- Android QR -->
            <div class="qr-code" ref="androidQrContainer">
              <canvas ref="androidQrCanvas" v-show="androidQrGenerated"></canvas>
              <div v-if="!androidQrGenerated" class="loading-placeholder">
                <div class="loading-spinner"></div>
                <div class="loading-text">{{ t('generatingQR') }}</div>
              </div>
            </div>

            <!-- iOS QR -->
            <!-- iOS 二维码 -->
            <div class="qr-code" ref="iosQrContainer">
              <canvas ref="iosQrCanvas" v-show="iosQrGenerated"></canvas>
              <div v-if="!iosQrGenerated" class="loading-placeholder">
                <div class="loading-spinner"></div>
                <div class="loading-text">{{ t('generatingQR') }}</div>
              </div>
            </div>
          </div>

          <div class="instructions">
            <h3>{{ t('downloadInstructions') }}:</h3>
            <ol>
              <li><strong>{{ t('androidUsers') }}</strong>: {{ t('androidInstruction') }}</li>
              <li><strong>{{ t('iosUsers') }}</strong>: {{ t('iosInstruction') }}</li>
              <li>{{ t('troubleshooting') }}</li>
            </ol>
          </div>

          <div class="device-selector">
            <button class="device-btn android-btn" :disabled="!appData.androidUrl" @click="downloadAndroid">
              <i class="fab fa-android"></i> {{ t('downloadAndroid') }}
            </button>
            <button class="device-btn ios-btn" :disabled="!appData.iosUrl" @click="downloadIOS">
              <i class="fab fa-apple"></i> {{ t('goToAppStore') }}
            </button>
          </div>
        </div>
      </div>

      <footer>
        <p>© 2026 Genius Ring. {{ t('allRightsReserved') }}</p>
        <!-- <p>{{ t('technicalSupport') }}: 400-888-8888 | {{ t('email') }}: support@example.com</p>
        <p>{{ t('trademarks') }}</p> -->
      </footer>
    </div>

    <!-- Refresh Button -->
    <button class="refresh-btn" @click="refreshAppData" :title="t('refreshData')">
      <i class="fas fa-sync-alt"></i>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch, onBeforeUnmount } from 'vue'
import { fetchAndroidData, fetchIOSData, fetchPrefix } from './api/appApi'
import QRCode from 'qrcode'

// Language State
const currentLang = ref(localStorage.getItem('app-language') || 'zh')
const langText = computed(() => currentLang.value === 'zh' ? 'English' : '中文')

// Translations
const translations = {
  zh: {
    appDownloadCenter: '应用下载中心',
    scanToDownload: '扫描二维码，即刻下载体验全新应用',
    platformDescription: 'Android 手机扫码直接下载 APK，iOS 手机扫码跳转 App Store',
    appScreenshots: '应用截图',
    screenshotAlt: '应用截图',
    downloadApp: '下载应用',
    appName: '应用名称',
    version: '版本',
    platform: '平台',
    updated: '更新时间',
    forceUpdate: '强制更新',
    silentUpdate: '静默更新',
    androidDownload: 'Android 下载',
    iosDownload: 'iOS 下载',
    generatingQR: '生成二维码中...',
    scanToDownloadAPK: '扫描下载 APK 安装包',
    scanToAppStore: '扫描跳转 App Store',
    scanHint: '使用相机或微信扫描',
    downloadInstructions: '下载指引',
    androidUsers: 'Android 用户',
    androidInstruction: '使用微信或系统相机扫描左侧二维码，下载 APK 文件后安装',
    iosUsers: 'iOS 用户',
    iosInstruction: '使用相机或微信扫描右侧二维码，直接跳转到 App Store 下载',
    troubleshooting: '如遇到下载问题，请尝试在系统浏览器中打开链接',
    downloadAndroid: '直接下载 Android 版',
    goToAppStore: '前往 App Store',
    allRightsReserved: '保留所有权利',
    technicalSupport: '技术支持',
    email: '邮箱',
    trademarks: 'Android 和 iOS 是各自所有者的商标',
    apiConnecting: 'API 连接中...',
    apiConnected: 'API 已连接',
    apiError: 'API 连接失败',
    downloadLinkInvalid: '下载链接无效，请稍后重试',
    qrGenerationFailed: '二维码生成失败，请使用下载按钮',
    loadingAppInfo: '正在加载应用信息...',
    refreshData: '刷新应用数据'
  },
  en: {
    appDownloadCenter: 'App Download Center',
    scanToDownload: 'Scan QR code to download and experience the new app',
    platformDescription: 'Android users scan to download APK, iOS users scan to jump to App Store',
    appScreenshots: 'App Screenshots',
    screenshotAlt: 'App Screenshot',
    downloadApp: 'Download App',
    appName: 'App Name',
    version: 'Version',
    platform: 'Platform',
    updated: 'Updated',
    forceUpdate: 'Force Update',
    silentUpdate: 'Silent Update',
    androidDownload: 'Android Download',
    iosDownload: 'iOS Download',
    generatingQR: 'Generating QR code...',
    scanToDownloadAPK: 'Scan to download APK',
    scanToAppStore: 'Scan to jump to App Store',
    scanHint: 'Use camera or WeChat to scan',
    downloadInstructions: 'Download Instructions',
    androidUsers: 'Android Users',
    androidInstruction: 'Use WeChat or system camera to scan the left QR code, then download and install the APK file',
    iosUsers: 'iOS Users',
    iosInstruction: 'Use camera or WeChat to scan the right QR code, directly jump to App Store to download',
    troubleshooting: 'If you encounter download issues, try opening the link in the system browser',
    downloadAndroid: 'Download Android Version',
    goToAppStore: 'Go to App Store',
    allRightsReserved: 'All rights reserved',
    technicalSupport: 'Technical Support',
    email: 'Email',
    trademarks: 'Android and iOS are trademarks of their respective owners',
    apiConnecting: 'API Connecting...',
    apiConnected: 'API Connected',
    apiError: 'API Connection Failed',
    downloadLinkInvalid: 'Download link invalid, please try again later',
    qrGenerationFailed: 'QR code generation failed, please use download button',
    loadingAppInfo: 'Loading app information...',
    refreshData: 'Refresh App Data'
  }
}

const t = (key) => translations[currentLang.value]?.[key] || key

// API Status
const apiStatus = ref({ class: 'disconnected', text: 'apiConnecting' })

// App Data
const appData = ref({
  appName: 'Genius Ring',
  description: 'Loading...',
  version: '',
  versionCode: 0,
  platform: '',
  androidDate: '',
  iosDate: '',
  androidUrl: '',
  iosUrl: '',
  forceUpdate: 0,
  silentUpdate: 0
})

// QR State
const androidQrGenerated = ref(false)
const iosQrGenerated = ref(false)
const androidError = ref('')
const iosError = ref('')

// 使用新的 ref 名称避免冲突
const androidQrContainer = ref(null)
const iosQrContainer = ref(null)

// 添加 canvas 引用
const androidQrCanvas = ref(null)
const iosQrCanvas = ref(null)

// Screenshots
const screenshots = ref([])
const currentScreenshotIndex = ref(0)
const currentScreenshot = computed(() => screenshots.value[currentScreenshotIndex.value])
let screenshotInterval = null

const iosScreenshots = ref([])
const iosCurrentScreenshotIndex = ref(0)
const iosCurrentScreenshot = computed(() => iosScreenshots.value[iosCurrentScreenshotIndex.value])
let iosScreenshotInterval = null

// Computed
// const appDescription = computed(() => appData.value.description || t('loadingAppInfo'))
const formattedUpdateTime = computed(() => {
  try {
    const androidDate = new Date(appData.value.androidDate)
    const iosDate = new Date(appData.value.iosDate)
    return 'Android:' + androidDate.toLocaleDateString(currentLang.value === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    }) + ' / IOS:' + iosDate.toLocaleDateString(currentLang.value === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    })
  } catch (e) {
    return new Date().toISOString()
  }
})

// Methods
const toggleLanguage = () => {
  currentLang.value = currentLang.value === 'zh' ? 'en' : 'zh'
  localStorage.setItem('app-language', currentLang.value)
}

const updateApiStatus = (status, messageKey) => {
  apiStatus.value = {
    class: status,
    text: messageKey || (status === 'connected' ? 'apiConnected' : 'apiError')
  }
}

const changeScreenshot = (index) => {
  currentScreenshotIndex.value = index
}

const iosChangeScreenshot = (index) => {
  iosCurrentScreenshotIndex.value = index
}

const downloadAndroid = () => {
  if (appData.value.androidUrl) window.open(appData.value.androidUrl, '_blank')
}

const downloadIOS = () => {
  if (appData.value.iosUrl) window.open(appData.value.iosUrl, '_blank')
}

// 修复后的 generateQRCode 函数
const generateQRCode = async (containerRef, canvasRef, text, platform) => {
  if (!text || text.trim() === '') {
    const errorMsg = t('downloadLinkInvalid')
    if (platform === 'android') {
      androidError.value = errorMsg
    } else {
      iosError.value = errorMsg
    }
    return false
  }

  try {
    // 等待 DOM 就绪
    await nextTick()

    // 从 ref 获取 canvas 元素
    const canvas = canvasRef.value
    if (!canvas) {
      throw new Error('Canvas 元素未找到')
    }

    // 清空 canvas
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // 直接在 canvas 上生成二维码
    await QRCode.toCanvas(canvas, text, {
      width: 160,
      height: 160,
      margin: 1,
      color: {
        dark: platform === 'android' ? '#3498db' : '#2ecc71',
        light: '#ffffff'
      },
      errorCorrectionLevel: 'H'
    })

    // 更新状态
    if (platform === 'android') {
      androidQrGenerated.value = true
      androidError.value = ''
    } else {
      iosQrGenerated.value = true
      iosError.value = ''
    }

    return true
  } catch (err) {
    console.error(`生成 ${platform} 二维码失败:`, err)
    const errorMsg = t('qrGenerationFailed')
    if (platform === 'android') {
      androidError.value = errorMsg
    } else {
      iosError.value = errorMsg
    }
    return false
  }
}

// 更新 generateAllQRCodes
const generateAllQRCodes = async () => {
  await nextTick()

  // 重置状态
  androidQrGenerated.value = false
  iosQrGenerated.value = false

  // 生成二维码
  if (appData.value.androidUrl) {
    await generateQRCode(androidQrContainer, androidQrCanvas, appData.value.androidUrl, 'android')
  }

  if (appData.value.iosUrl) {
    await generateQRCode(iosQrContainer, iosQrCanvas, appData.value.iosUrl, 'ios')
  }
}

const showDeviceHint = (message) => {
  const hint = document.createElement('div')
  hint.style.cssText = `
    position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
    background: rgba(52, 152, 219, 0.9); color: white; padding: 15px 20px;
    border-radius: 10px; z-index: 1000; box-shadow: 0 5px 15px rgba(0,0,0,0.2);
    max-width: 90%; text-align: center; animation: fadeInOut 5s forwards;
  `

  // 检查是否已存在相同的样式
  if (!document.querySelector('#hint-style')) {
    const style = document.createElement('style')
    style.id = 'hint-style'
    style.textContent = `
      @keyframes fadeInOut {
        0% { opacity: 0; top: 0px; } 
        10% { opacity: 1; top: 20px; }
        90% { opacity: 1; top: 20px; } 
        100% { opacity: 0; top: 0px; }
      }
    `
    document.head.appendChild(style)
  }

  hint.textContent = message
  document.body.appendChild(hint)
  setTimeout(() => {
    if (hint.parentNode) {
      hint.parentNode.removeChild(hint)
    }
  }, 5000)
}

const detectDeviceAndShowHint = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera
  const isAndroid = /android/i.test(userAgent)
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream

  if (isAndroid) {
    showDeviceHint(currentLang.value === 'zh'
      ? '检测到您使用的是 Android 设备，建议扫描左侧二维码下载 APK'
      : 'Detected that you are using an Android device, recommend scanning the left QR code to download APK')
  } else if (isIOS) {
    showDeviceHint(currentLang.value === 'zh'
      ? '检测到您使用的是 iOS 设备，建议扫描右侧二维码跳转 App Store'
      : 'Detected that you are using an iOS device, recommend scanning the right QR code to jump to App Store')
  }
}

const initApp = async () => {
  updateApiStatus('disconnected', 'apiConnecting')
  try {
    const androidData = await fetchAndroidData()
    if (androidData) {
      appData.value.version = appData.value.version == null ? androidData.version + '(' + androidData.versionCode + ')' : 'Android:' + androidData.version + '(' + androidData.versionCode + ')'
      appData.value.platform = androidData.platform
      appData.value.androidDate = androidData.uploadDate
      if (androidData.downloadUrl) {
        appData.value.androidUrl = androidData.downloadUrl.startsWith('http')
          ? androidData.downloadUrl
          : `https://ring.shcj.xyz/api${androidData.downloadUrl}`
      }
      let appStoreScreenshot = androidData.appStoreScreenshot
      if (appStoreScreenshot) {
        const screenshotUrls = appStoreScreenshot.split(',')
        screenshotUrls.forEach((url) => {
          if (url.trim()) {
            screenshots.value.push(fetchPrefix() + url.trim())
          }
        })
      }
      console.log(screenshots.value);
      
      updateApiStatus('connected', 'apiConnected')
    } else {
      updateApiStatus('disconnected', 'apiError')
    }

    const iosData = await fetchIOSData()
    if (iosData && iosData.filePath) {
      appData.value.version = appData.value.version == null ? 'IOS:' + iosData.version + '(' + iosData.versionCode + ')' : appData.value.version + ' / IOS:' + iosData.version + '(' + iosData.versionCode + ')'
      appData.value.platform = appData.value.platform == null ? iosData.platform : appData.value.platform + ' / ' + iosData.platform
      appData.value.iosUrl = iosData.filePath
      appData.value.iosDate = iosData.uploadDate
      let appStoreScreenshot = iosData.appStoreScreenshot
      if (appStoreScreenshot) {
        const screenshotUrls = appStoreScreenshot.split(',')
        screenshotUrls.forEach((url) => {
          if (url.trim()) {
            iosScreenshots.value.push(fetchPrefix() + url.trim())
          }
        })
      }
      console.log(screenshots.value);
    }
  } catch (error) {
    console.error('Failed to initialize app:', error)
    updateApiStatus('disconnected', 'apiError')
  }
}
// 修改 refreshAppData 函数
const refreshAppData = async () => {
  await initApp()
  await generateAllQRCodes()
  alert(currentLang.value === 'zh' ? '应用数据已刷新' : 'App data refreshed')
}

// 添加清理函数
const cleanup = () => {
  if (screenshotInterval) {
    clearInterval(screenshotInterval)
    screenshotInterval = null
  }

  if (iosScreenshotInterval) {
    clearInterval(iosScreenshotInterval)
    iosScreenshotInterval = null
  }

  // 清理 canvas 元素
  if (androidQrCanvas.value) {
    androidQrCanvas.value = null
  }
  if (iosQrCanvas.value) {
    iosQrCanvas.value = null
  }
}

onMounted(async () => {
  try {
    await initApp()
    await generateAllQRCodes()
    detectDeviceAndShowHint()

    screenshotInterval = setInterval(() => {
      currentScreenshotIndex.value = (currentScreenshotIndex.value + 1) % screenshots.value.length
    }, 4000)
    iosScreenshotInterval = setInterval(() => {
      iosCurrentScreenshotIndex.value = (iosCurrentScreenshotIndex.value + 1) % iosScreenshots.value.length
    }, 4000)
  } catch (error) {
    console.error('Error in onMounted:', error)
  }
})

// 使用 onBeforeUnmount 替代 onUnmounted
onBeforeUnmount(() => {
  cleanup()
})

// 添加对 appData 的监听，当链接变化时重新生成二维码
watch(() => [appData.value.androidUrl, appData.value.iosUrl], () => {
  if (androidQrContainer.value && iosQrContainer.value) {
    generateAllQRCodes()
  }
})
</script>

<style scoped>
/* Keep all the same styles from before */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.app-container {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  color: #333;
  line-height: 1.6;
  min-height: 100vh;
  padding: 20px;
  position: relative;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

.language-switcher {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
}

.lang-btn {
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid #3498db;
  border-radius: 30px;
  padding: 8px 20px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.lang-btn:hover {
  background: #3498db;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
}

header {
  text-align: center;
  padding: 30px 20px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

h1 {
  color: #2c3e50;
  font-size: 2.8rem;
  margin-bottom: 10px;
  background: linear-gradient(90deg, #3498db, #2ecc71);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.subtitle {
  font-size: 1.2rem;
  color: #7f8c8d;
  margin-bottom: 20px;
}

.app-info {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  margin-bottom: 40px;
}

.screenshot-section,
.download-section {
  flex: 1;
  min-width: 300px;
  background: white;
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 1.8rem;
  color: #2c3e50;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f1f1f1;
}

.screenshot-container {
  position: relative;
  overflow: hidden;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.screenshot {
  width: 100%;
  display: block;
  transition: transform 0.5s ease;
}

.screenshot:hover {
  transform: scale(1.03);
}

.device-frame {
  position: relative;
  /* max-width: 280px; */
  margin: 0 auto;
}

.device-frame::before,
.device-frame::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  height: 30px;
  background: #222;
  z-index: 2;
}

.device-frame::before {
  top: 0;
  border-radius: 15px 15px 0 0;
}

.device-frame::after {
  bottom: 0;
  border-radius: 0 0 15px 15px;
}

.screenshot-nav {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 10px;
}

.nav-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ddd;
  cursor: pointer;
  transition: all 0.3s;
}

.nav-dot.active {
  background: #3498db;
  transform: scale(1.2);
}

.app-description {
  margin-bottom: 30px;
  color: #555;
  font-size: 1.1rem;
}

.qr-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  gap: 20px;
  margin-bottom: 30px;
}

.qr-item {
  text-align: center;
  padding: 20px;
  border-radius: 15px;
  transition: all 0.3s;
}

.qr-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.android-qr {
  background: rgba(52, 152, 219, 0.1);
  border: 2px solid #3498db;
}

.ios-qr {
  background: rgba(46, 204, 113, 0.1);
  border: 2px solid #2ecc71;
}

.qr-title {
  font-size: 1.3rem;
  margin-bottom: 15px;
  color: #2c3e50;
}

.qr-code {
  width: 180px;
  height: 180px;
  margin: 0 auto 15px;
  padding: 10px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-code canvas {
  width: 100%;
  height: 100%;
}

.loading-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

.loading-text {
  color: #7f8c8d;
  font-size: 0.9rem;
}

.error-message {
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 10px;
}

.instructions {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 15px;
  margin-top: 20px;
}

.instructions h3 {
  color: #2c3e50;
  margin-bottom: 15px;
}

.instructions ol {
  padding-left: 20px;
}

.instructions li {
  margin-bottom: 10px;
}

.device-selector {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
}

.device-btn {
  padding: 12px 25px;
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s;
}

.android-btn {
  background: #3498db;
  color: white;
}

.ios-btn {
  background: #2ecc71;
  color: white;
}

.device-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 7px 15px rgba(0, 0, 0, 0.2);
}

.device-btn:disabled {
  background: #bdc3c7;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

footer {
  text-align: center;
  padding: 30px 20px;
  margin-top: 50px;
  color: #7f8c8d;
  border-top: 1px solid #eee;
}

.scan-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
  color: #3498db;
  font-weight: 600;
}

.download-info {
  margin-top: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
  font-size: 0.9rem;
}

.download-info p {
  margin: 5px 0;
}

.version-info {
  color: #3498db;
  font-weight: bold;
}

.size-info {
  color: #2ecc71;
}

.update-time {
  color: #7f8c8d;
  font-style: italic;
}

.api-status {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  margin-left: 10px;
}

.api-status.connected {
  background: #d5f4e6;
  color: #27ae60;
}

.api-status.disconnected {
  background: #fadbd8;
  color: #e74c3c;
}

.force-update-badge,
.silent-update-badge {
  display: inline-block;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  margin-left: 8px;
  font-weight: bold;
}

.force-update-badge {
  background: #e74c3c;
}

.silent-update-badge {
  background: #3498db;
}

.update-badges {
  display: flex;
  gap: 5px;
  margin-top: 5px;
  justify-content: center;
}

.refresh-btn {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  font-size: 1.2rem;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.refresh-btn:hover {
  transform: rotate(90deg);
  background: #2980b9;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  h1 {
    font-size: 2.2rem;
  }

  .qr-container {
    flex-direction: column;
    align-items: center;
  }

  .device-selector {
    flex-direction: column;
    align-items: center;
  }

  .device-btn {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }

  .language-switcher {
    position: relative;
    top: 0;
    right: 0;
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
  }

  .lang-btn {
    position: static;
  }
}
</style>